import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useWedding } from '../context/WeddingContext'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { notifyCouple } from '../lib/notifications'
import { Heart, Lock, Check, Pencil, ChevronDown } from 'lucide-react'
import { Sprig } from './ornaments'

// Curated marriage-prep prompts — warm, real, and aware of the things that
// actually matter (money, family, faith, in-laws, dreams). Keys are stable so
// reordering/adding later never disturbs saved answers.
const QUESTIONS = [
  { key: 'q1',  text: 'What does "home" feel like to you?' },
  { key: 'q2',  text: 'How did your parents handle money — and what would you do differently?' },
  { key: 'q3',  text: "What's one tradition from your family you want to carry into ours?" },
  { key: 'q4',  text: 'Picture us ten years from now on a normal Sunday. What are we doing?' },
  { key: 'q5',  text: "How do you most like to be comforted when you're stressed?" },
  { key: 'q6',  text: 'What does a fair argument between us look like?' },
  { key: 'q7',  text: 'What role do you want faith to play in our home?' },
  { key: 'q8',  text: 'What kind of parents do we want to be?' },
  { key: 'q9',  text: 'How should we split the responsibilities of running a home?' },
  { key: 'q10', text: 'What does financial success look like to you in five years?' },
  { key: 'q11', text: 'Whose advice do we welcome — and whose do we keep our marriage away from?' },
  { key: 'q12', text: "What's a dream you've never said out loud?" },
  { key: 'q13', text: 'How do we want to handle extended-family expectations together?' },
  { key: 'q14', text: 'What makes you feel most loved by me?' },
  { key: 'q15', text: 'How do we protect "us" when life gets busy?' },
  { key: 'q16', text: 'What is one fear you have about marriage?' },
  { key: 'q17', text: 'How should we make the big decisions, together?' },
  { key: 'q18', text: 'What does real rest look like for you?' },
  { key: 'q19', text: 'What is one money habit we should start now?' },
  { key: 'q20', text: 'How do we want to celebrate our wins?' },
  { key: 'q21', text: 'What kind of in-law do you hope to be?' },
  { key: 'q22', text: 'What part of your day do you most want to share with me?' },
  { key: 'q23', text: 'When did you first know you wanted to marry me?' },
  { key: 'q24', text: 'When we are old, what do we hope people say about our marriage?' },
]

function currentQuestion(createdAt) {
  const start = createdAt ? new Date(createdAt) : new Date()
  const weeks = Math.floor((Date.now() - start.getTime()) / (86400000 * 7))
  return QUESTIONS[((weeks % QUESTIONS.length) + QUESTIONS.length) % QUESTIONS.length]
}

const card = { background: 'linear-gradient(135deg,#FFFFFF,#FBF6F1)', border: '1px solid #ECE2D8', borderRadius: '1.25rem', padding: '1.5rem' }

export default function QuestionOfWeek() {
  const { wedding } = useWedding()
  const { user } = useAuth()
  const [rows, setRows] = useState(null)   // null = loading
  const [text, setText] = useState('')
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [open, setOpen] = useState(false)   // collapsed by default — tap to expand

  const q = wedding ? currentQuestion(wedding.created_at) : null

  // Hide once the wedding has passed — this is an engagement-season ritual.
  const days = wedding?.wedding_date ? Math.ceil((new Date(wedding.wedding_date) - new Date()) / 86400000) : 0
  const past = days < 0

  useEffect(() => {
    if (!wedding?.id || !q) return
    supabase.from('weekly_reflections').select('*').eq('wedding_id', wedding.id).eq('prompt_key', q.key)
      .then(({ data }) => setRows(data || []))
  }, [wedding?.id, q?.key])

  if (!wedding || !q || past) return null

  const mine    = rows?.find(r => r.user_id === user?.id)
  const theirs  = rows?.find(r => r.user_id !== user?.id)
  const isP1    = wedding.partner1_id === user?.id
  const partnerName  = (isP1 ? wedding.partner2_name : wedding.partner1_name)?.split(' ')[0] || 'your partner'
  const partnerLinked = !!wedding.partner2_id
  const bothAnswered  = !!mine && !!theirs

  async function save() {
    if (!text.trim()) return
    setSaving(true)
    await supabase.from('weekly_reflections').upsert(
      { wedding_id: wedding.id, prompt_key: q.key, user_id: user.id, answer: text.trim(), updated_at: new Date().toISOString() },
      { onConflict: 'wedding_id,prompt_key,user_id' }
    )
    // If the partner is linked and hasn't answered yet, nudge them so the
    // reveal actually happens instead of one person answering and forgetting.
    if (partnerLinked && !theirs) {
      const partnerId = isP1 ? wedding.partner2_id : wedding.partner1_id
      const myFirst = (isP1 ? wedding.partner1_name : wedding.partner2_name)?.split(' ')[0] || 'Your partner'
      notifyCouple(wedding.id, `${myFirst} answered this week's question`, 'Open Okoiya to write yours, then reveal your answers together 💛', undefined, partnerId)
    }
    const { data } = await supabase.from('weekly_reflections').select('*').eq('wedding_id', wedding.id).eq('prompt_key', q.key)
    setRows(data || []); setEditing(false); setSaving(false)
  }

  // A one-line status so the collapsed card still tells you where things stand
  const collapsedHint = bothAnswered ? 'You both answered — tap to revisit 💛'
    : mine ? (partnerLinked ? `Waiting on ${partnerName} — tap to view` : 'You answered — tap to view')
    : 'Tap to answer together'

  return (
    <div style={card}>
      {/* Header — tap to expand / collapse */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', display: 'block' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.5rem' }}>
          <Sprig size={18} />
          <span style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#C4826A' }}>Question of the Week</span>
          <ChevronDown size={18} color="#C4826A" style={{ marginLeft: 'auto', transition: 'transform 0.25s', transform: open ? 'rotate(180deg)' : 'none' }} />
        </div>

        {/* The question (always visible as a teaser) */}
        <p style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: 'clamp(1.15rem,2.4vw,1.45rem)', color: '#1A1A1A', lineHeight: 1.35, margin: open ? '0 0 1rem' : 0 }}>
          {q.text}
        </p>

        {!open && (
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.74rem', color: '#9B9B9B', margin: '0.5rem 0 0', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            {bothAnswered ? <Check size={12} color="#9BAF9B" /> : <Heart size={12} color="#C4826A" />} {collapsedHint}
          </p>
        )}
      </button>

      {!open ? null : rows === null ? (
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.8rem', color: '#BCBCBC', margin: 0 }}>One moment…</p>
      ) : (!mine || editing) ? (
        /* ── Write your answer ── */
        <div>
          <textarea
            value={text} onChange={e => setText(e.target.value)} rows={3}
            placeholder="Write your honest answer — just for the two of you…"
            style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.9rem', lineHeight: 1.6, color: '#1A1A1A', padding: '0.75rem 0.875rem', border: '1.5px solid #E5E0D8', borderRadius: '0.75rem', outline: 'none', resize: 'vertical' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginTop: '0.625rem', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.7rem', color: '#9B9B9B' }}>
              <Lock size={11} /> {partnerLinked ? `Hidden until ${partnerName} answers too` : 'Private to the two of you'}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {editing && <button onClick={() => { setEditing(false); setText('') }} className="btn-secondary" style={{ fontSize: '0.78rem', padding: '0.45rem 0.875rem' }}>Cancel</button>}
              <button onClick={save} disabled={saving || !text.trim()} className="btn-primary" style={{ fontSize: '0.78rem', padding: '0.45rem 1rem' }}>
                {saving ? 'Saving…' : 'Save my answer'}
              </button>
            </div>
          </div>
        </div>
      ) : bothAnswered ? (
        /* ── Reveal: both answers ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {[{ r: mine, who: 'You', accent: '#C4826A' }, { r: theirs, who: partnerName, accent: '#9BAF9B' }].map(({ r, who, accent }, i) => (
            <div key={i} style={{ background: 'white', border: '1px solid #EFE7DD', borderRadius: '0.875rem', padding: '0.875rem 1rem' }}>
              <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: accent, margin: '0 0 0.3rem' }}>{who}</p>
              <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.88rem', color: '#3A3A3A', lineHeight: 1.65, margin: 0, whiteSpace: 'pre-wrap' }}>{r.answer}</p>
            </div>
          ))}
          <button onClick={() => { setText(mine.answer); setEditing(true) }} style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', color: '#9B9B9B', padding: 0 }}>
            <Pencil size={11} /> Edit my answer
          </button>
        </div>
      ) : (
        /* ── You answered, waiting on partner ── */
        <div>
          <div style={{ background: 'white', border: '1px solid #EFE7DD', borderRadius: '0.875rem', padding: '0.875rem 1rem', marginBottom: '0.625rem' }}>
            <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#C4826A', margin: '0 0 0.3rem' }}>You · saved <Check size={11} style={{ verticalAlign: 'middle' }} /></p>
            <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.88rem', color: '#3A3A3A', lineHeight: 1.65, margin: 0, whiteSpace: 'pre-wrap' }}>{mine.answer}</p>
          </div>
          {partnerLinked && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', background: 'rgba(155,175,155,0.08)', border: '1px dashed rgba(155,175,155,0.4)', borderRadius: '0.875rem', padding: '0.75rem 1rem' }}>
              <Heart size={14} color="#9BAF9B" />
              <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.8rem', color: '#6B6B6B', margin: 0, lineHeight: 1.5 }}>
                {partnerName}'s answer will appear here once they write theirs. We have let them know it is their turn 💛
              </p>
            </div>
          )}
          <button onClick={() => { setText(mine.answer); setEditing(true) }} style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', color: '#9B9B9B', padding: 0 }}>
            <Pencil size={11} /> Edit my answer
          </button>
        </div>
      )}

      {/* Connect-accounts nudge — only while the partner has not joined yet */}
      {open && partnerLinked === false && rows !== null && (
        <div style={{ marginTop: '0.875rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem', background: 'rgba(196,130,106,0.06)', border: '1px solid rgba(196,130,106,0.2)', borderRadius: '0.875rem', padding: '0.75rem 1rem' }}>
          <Heart size={14} color="#C4826A" style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.78rem', color: '#6B6B6B', margin: 0, lineHeight: 1.55 }}>
            Connect your partner's account so you can both manage your wedding and answer these questions together.{' '}
            <Link to="/dashboard/settings" style={{ color: '#C4826A', fontWeight: 700, textDecoration: 'none' }}>Invite them in Settings →</Link>
          </p>
        </div>
      )}
    </div>
  )
}
